/* mo-tier-draw.js: the step-up drawing of the tier articles (sections/mo-tier.liquid), Mattress Overstock, 2026-10-15.
   One bed per tier stands on one floor line at one scale (px per inch), each at the height its maker prints. Where the
   maker prints layer depths (Beautyrest's dealer sheets) every layer is drawn at its depth; where the printed layers add
   up to more or less than the printed height, the layers above the coil core are drawn in proportion so the bed keeps
   its printed height. Where a maker prints no depths (Stearns & Foster) the bed is drawn at its true height with
   "Layer depths not published" and the layer names in order.
   Between neighbours: ribbons join the same material in both beds, a red change bar marks what the upper tier adds, and
   a riser gives the height step. svg(), caption() and list() are pure string functions, so build_still.js runs the same
   code in Node to write the server still and the live drawing lands on it. Plain JS, no library. */
(function (root) {
  'use strict';

  var COL = { cover: '#f3efe8', foam: '#e6dccb', mem: '#a7b9c8', latex: '#e3cf8f', micro: '#3a3633', core: '#2b2927', base: '#77706a' };
  var WORD = { cover: 'Cover', foam: 'Comfort foam', mem: 'Memory foam', latex: 'Latex', micro: 'Micro coils', core: 'Coils', base: 'Base foam' };

  function esc(t) { return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function r1(v) { return Math.round(v * 10) / 10; }
  function num(v) { return (Math.round(v * 100) / 100).toString(); }
  function inch(v) { return num(v) + ' in.'; }

  // the same material in two beds: printed names differ only in these words
  function canon(name, kind) {
    var n = String(name).toLowerCase().replace(/[®™]/g, '').replace(/\s+/g, ' ').trim();
    if (kind === 'cover') return 'cover';
    if (kind === 'core') return 'core';
    if (kind === 'micro') return 'micro coils';
    return n.replace(/ technology$/, '');
  }

  function G(narrow, n, maxH) {
    if (narrow) {
      var bw = n > 3 ? 62 : 76, x0 = 24, avail = 360 - x0 - 4, gap = (avail - n * bw) / (n - 1);
      var s = Math.min(9, 172 / maxH), top = 40;
      return { W: 360, x0: x0, bw: bw, gap: gap, s: s, top: top, floor: top + maxH * s + 4, under: 22, f1: 12, f2: 11, narrow: true };
    }
    var bw2 = n > 3 ? 118 : 150, x02 = 44, avail2 = 720 - x02 - 8, gap2 = (avail2 - n * bw2) / (n - 1);
    var s2 = Math.min(14, 236 / maxH), top2 = 48;
    return { W: 720, x0: x02, bw: bw2, gap: gap2, s: s2, top: top2, floor: top2 + maxH * s2 + 6, under: 30, f1: 15, f2: 12.5, narrow: false };
  }

  // layers of one bed, bottom up, with the depth they are drawn at
  function stack(bed) {
    var L = bed.layers || [], i, out = [], below = 0, above = 0, coreSeen = false, sum;
    var printed = 0, depths = L.some(function (l) { return l[2] !== 'cover' && l[1] != null; });
    if (!depths) return { depths: false, list: [], f: 1, printed: 0 };
    for (i = L.length - 1; i >= 0; i--) {
      if (L[i][2] === 'cover' || L[i][1] == null) continue;
      printed += L[i][1];
      if (!coreSeen) below += L[i][1]; else above += L[i][1];
      if (L[i][2] === 'core') coreSeen = true;
    }
    // below = base + core (everything up to and including the coil core); above = the comfort layers
    var f = above > 0 ? Math.max(0.5, (bed.height - below) / above) : 1;
    if (Math.abs(printed - bed.height) < 0.051) f = 1;
    var seen = {}, past = false, y = 0;
    for (i = L.length - 1; i >= 0; i--) {
      var l = L[i];
      if (l[2] === 'cover' || l[1] == null) continue;
      var k = canon(l[0], l[2]);
      seen[k] = (seen[k] || 0) + 1;
      var d = past ? l[1] * f : l[1];
      out.push({ name: l[0], kind: l[2], d: l[1], dd: d, y0: y, key: k, occ: seen[k], n: i + 1 });
      y += d;
      if (l[2] === 'core') past = true;
    }
    return { depths: true, list: out, f: f, printed: printed };
  }

  // which layers of `up` are not in `low` (by material, counted): returns a set of "key|occ"
  function added(low, up) {
    var have = {}, i, out = {};
    if (!low || !up) return out;
    for (i = 0; i < low.list.length; i++) have[low.list[i].key] = Math.max(have[low.list[i].key] || 0, low.list[i].occ);
    for (i = 0; i < up.list.length; i++) if (up.list[i].occ > (have[up.list[i].key] || 0)) out[up.list[i].key + '|' + up.list[i].occ] = 1;
    return out;
  }

  function zig(cx, y0, y1, hw, n) {
    var pts = [], steps = n * 2, i;
    for (i = 0; i <= steps; i++) {
      var yy = y0 + (y1 - y0) * i / steps, xx = (i === 0 || i === steps) ? cx : cx + (i % 2 ? hw : -hw);
      pts.push(r1(xx) + ',' + r1(yy));
    }
    return pts.join(' ');
  }

  // one band; coils are drawn inside the band's own box so the band can move as one piece
  function band(k, x, w, g, uid, isNew, slot) {
    var y = g.floor - (k.y0 + k.dd) * g.s, h = k.dd * g.s, o = [], c;
    o.push('<g class="tl-l tl-' + k.kind + (isNew ? ' is-new' : '') + '" data-k="' + slot + '|' + esc(k.key) + '|' + k.occ + '" data-key="' + esc(k.key) + '" data-x="' + r1(x) + '" data-y="' + r1(y) + '" data-w="' + r1(w) + '" data-h="' + r1(h) + '">');
    o.push('<rect class="tl-b" x="' + r1(x) + '" y="' + r1(y) + '" width="' + r1(w) + '" height="' + r1(Math.max(h, 0.6)) + '" fill="' + COL[k.kind] + '"/>');
    if (k.kind === 'core') {
      var pitch = Math.max(8, 1.9 * g.s), nC = Math.floor((w - 2) / pitch), off = (w - nC * pitch) / 2, turns = Math.max(3, Math.round(k.d / 1.2));
      var id = 'tlc' + uid;
      o.push('<defs><g id="' + id + '"><rect fill="none" stroke="#4c4844" stroke-width=".8" x="' + r1(-pitch / 2 + 0.8) + '" y="' + r1(y + 1.4) + '" width="' + r1(pitch - 1.6) + '" height="' + r1(h - 2.8) + '" rx="' + r1(pitch / 3) + '"/>');
      for (c = -1; c <= 1; c++) o.push('<polyline fill="none" stroke="#d9dde1" stroke-width=".7" stroke-linejoin="round" points="' + zig(c * (g.narrow ? 0.9 : 1.3), y + 3.5, y + h - 3.5, pitch * 0.3, turns) + '"/>');
      o.push('</g></defs>');
      for (c = 0; c < nC; c++) o.push('<use href="#' + id + '" x="' + r1(x + off + pitch * c + pitch / 2) + '"/>');
    } else if (k.kind === 'micro') {
      var p2 = Math.max(3.6, 0.62 * g.s), m = Math.floor((w - 2) / p2), o2 = (w - m * p2) / 2, pts = [];
      for (c = 0; c < m; c++) pts.push('M' + zig(x + o2 + p2 * c + p2 / 2, y + 1.4, y + h - 1.4, p2 * 0.34, 3).replace(/ /g, ' L'));
      o.push('<path fill="none" stroke="' + (isNew ? '#ffb3ae' : '#c8cdd2') + '" stroke-width=".7" stroke-linejoin="round" d="' + pts.join(' ') + '"/>');
    } else if (k.kind === 'latex' && h > 5) {
      var dots = [], dx = Math.max(5, g.s * 0.55), r, cc;
      for (r = y + dx / 2; r < y + h - 1; r += dx) for (cc = x + dx / 2 + ((Math.round((r - y) / dx)) % 2 ? dx / 2 : 0); cc < x + w - 1; cc += dx) dots.push('M' + r1(cc) + ' ' + r1(r) + 'h.01');
      o.push('<path stroke="#b89a45" stroke-width="1.3" stroke-linecap="round" d="' + dots.join('') + '"/>');
    }
    if (isNew) o.push('<rect class="tl-bar" x="' + r1(x - (g.narrow ? 4 : 6)) + '" y="' + r1(y + 0.4) + '" width="' + (g.narrow ? 3 : 4) + '" height="' + r1(Math.max(h - 0.8, 1.2)) + '" rx="1"/>');
    // the layer's depth where the band is tall enough to read it (wide drawing only; the list has every layer)
    if (!g.narrow && h >= 15 && k.kind !== 'core' && k.kind !== 'micro') {
      var label = (k.kind === 'latex' ? 'Latex' : k.kind === 'base' ? 'Base' : WORD[k.kind]) + ' ' + inch(k.d);
      o.push('<text class="tl-in' + (k.kind === 'base' ? ' tl-in--d' : '') + '" x="' + r1(x + 7) + '" y="' + r1(y + h / 2 + 4) + '">' + esc(label) + '</text>');
    }
    if (k.kind === 'core' && !g.narrow) {
      o.push('<rect class="tl-tab" x="' + r1(x + w - 74) + '" y="' + r1(y + h - 22) + '" width="68" height="17" rx="3"/>');
      o.push('<text class="tl-in tl-in--c" x="' + r1(x + w - 40) + '" y="' + r1(y + h - 9.5) + '" text-anchor="middle">' + esc(inch(k.d) + ' coils') + '</text>');
    }
    o.push('</g>');
    return o.join('');
  }

  function ribbon(a, b, xa, xb, g, cls, key) {
    var ya0 = g.floor - (a.y0 + a.dd) * g.s, ya1 = g.floor - a.y0 * g.s, yb0 = g.floor - (b.y0 + b.dd) * g.s, yb1 = g.floor - b.y0 * g.s;
    var mx = (xa + xb) / 2;
    return '<path class="tl-rib tl-rib--' + cls + '" data-key="' + esc(key) + '" d="M' + r1(xa) + ' ' + r1(ya0) + ' C' + r1(mx) + ' ' + r1(ya0) + ' ' + r1(mx) + ' ' + r1(yb0) + ' ' + r1(xb) + ' ' + r1(yb0) +
      ' L' + r1(xb) + ' ' + r1(yb1) + ' C' + r1(mx) + ' ' + r1(yb1) + ' ' + r1(mx) + ' ' + r1(ya1) + ' ' + r1(xa) + ' ' + r1(ya1) + 'Z" fill="' + COL[a.kind] + '"/>';
  }

  function wrap(text, max) {
    var words = String(text).split(' '), lines = [], cur = '';
    words.forEach(function (w) { if ((cur + ' ' + w).trim().length > max && cur) { lines.push(cur); cur = w; } else cur = (cur + ' ' + w).trim(); });
    if (cur) lines.push(cur);
    return lines;
  }

  // fit-fix 2026-10-16: the step label sits in the gap between two beds. On a phone with four beds (Add Series Two) the
  // gap is 28 units and every label is wider (+1.75 in. is 47.7), so it ran into the height labels and the taller bed.
  // Draw it only where it fits the gap; the riser stays, and the step chips under the drawing give the step in words.
  // Width: Manrope 700 at the step size in assets/mo-tier-step.css (11.5px narrow, 13.5px wide), rounded up.
  function stepW(t, narrow) {
    var w = 0, i, c;
    for (i = 0; i < t.length; i++) { c = t.charAt(i); w += /[.\s]/.test(c) ? 0.3 : c === 'i' ? 0.28 : c === 'm' ? 0.92 : 0.62; }
    return w * (narrow ? 11.5 : 13.5);
  }

  // state: { look, narrow, maxH, slots: [{ tier: {key, name, short, status}, bed: {...}|null, empty: 'text' }] }
  function svg(st) {
    var n = st.slots.length, g = G(st.narrow, n, st.maxH), o = [], i, j, H = g.floor + g.under;
    var stacks = st.slots.map(function (sl) { return sl.bed && sl.bed.height ? stack(sl.bed) : null; });
    var xs = st.slots.map(function (sl, i2) { return g.x0 + i2 * (g.bw + g.gap); });
    o.push('<svg class="tl-svg" viewBox="0 0 ' + g.W + ' ' + r1(H) + '" width="' + g.W + '" height="' + r1(H) + '" role="img" aria-labelledby="tl-t-' + (st.narrow ? 'n' : 'w') + '" data-narrow="' + (st.narrow ? 1 : 0) + '">');
    o.push('<title id="tl-t-' + (st.narrow ? 'n' : 'w') + '">' + esc(st.slots.map(function (sl) { return sl.tier.name + (sl.bed && sl.bed.height ? ' ' + inch(sl.bed.height) : ', not drawn'); }).join('; ')) + '</title>');
    o.push('<defs><pattern id="tl-hatch-' + (st.narrow ? 'n' : 'w') + '" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><path d="M0 0v7" stroke="#55504b" stroke-width="1.2"/></pattern></defs>');
    // ruler: every 2 in., labels every 4 in.
    var rx = g.narrow ? 14 : 30;
    o.push('<g class="tl-ruler"><path d="M' + rx + ' ' + r1(g.floor) + 'V' + r1(g.floor - Math.ceil(st.maxH) * g.s) + '"/>');
    for (i = 2; i <= st.maxH + 0.01; i += 2) {
      var ty = g.floor - i * g.s;
      o.push('<path d="M' + (rx - (i % 4 ? 3 : 5)) + ' ' + r1(ty) + 'h' + (i % 4 ? 3 : 5) + '"/>');
      if (i % 4 === 0) o.push('<text x="' + (rx - 8) + '" y="' + r1(ty + 4) + '" text-anchor="end">' + i + '</text>');
    }
    o.push('<text x="' + (rx - 8) + '" y="' + r1(g.floor + 4) + '" text-anchor="end">in.</text></g>');
    // ribbons and risers first so the beds sit on top of them
    o.push('<g class="tl-links">');
    for (i = 1; i < n; i++) {
      var A = st.slots[i - 1], B = st.slots[i], sa = stacks[i - 1], sb = stacks[i];
      if (!A.bed || !B.bed || !A.bed.height || !B.bed.height) continue;
      var xa = xs[i - 1] + g.bw, xb = xs[i];
      if (sa && sb && sa.depths && sb.depths) {
        for (j = 0; j < sb.list.length; j++) {
          var kb = sb.list[j], ka = null;
          for (var q = 0; q < sa.list.length; q++) if (sa.list[q].key === kb.key && sa.list[q].occ === kb.occ) ka = sa.list[q];
          if (ka) o.push(ribbon(ka, kb, xa, xb, g, kb.kind, kb.key));
        }
      }
      // riser: the step in height
      var ta = g.floor - A.bed.height * g.s, tb = g.floor - B.bed.height * g.s, dh = Math.round((B.bed.height - A.bed.height) * 100) / 100;
      o.push('<path class="tl-guide" d="M' + r1(xa) + ' ' + r1(ta) + 'H' + r1(xb) + '"/>');
      if (dh !== 0) o.push('<path class="tl-riser" d="M' + r1(xb - 3) + ' ' + r1(ta) + 'H' + r1(xb + 3) + 'M' + r1(xb) + ' ' + r1(ta) + 'V' + r1(tb) + 'M' + r1(xb - 3) + ' ' + r1(tb) + 'H' + r1(xb + 3) + '"/>');
      var lx = (xa + xb) / 2, ly = Math.max(ta, tb) - (g.narrow ? 5 : 8);
      var stepT = dh > 0 ? '+' + inch(dh) : dh < 0 ? '−' + inch(-dh) : 'same';
      if (stepW(stepT, g.narrow) <= g.gap - 2) o.push('<text class="tl-step' + (dh > 0 ? '' : ' tl-step--flat') + '" x="' + r1(lx) + '" y="' + r1(ly) + '" text-anchor="middle">' + stepT + '</text>');
    }
    o.push('</g>');
    // the beds
    for (i = 0; i < n; i++) {
      var sl = st.slots[i], x = xs[i], mid = x + g.bw / 2, bed = sl.bed, sk = stacks[i];
      o.push('<g class="tl-bed' + (sl.tier.status === 'gone' ? ' tl-bed--gone' : '') + '" data-slot="' + i + '" data-tier="' + esc(sl.tier.key) + '">');
      if (bed && bed.height) o.push('<ellipse class="tl-shadow" cx="' + r1(mid) + '" cy="' + r1(g.floor + 1) + '" rx="' + r1(g.bw * 0.56) + '" ry="' + (g.narrow ? 3 : 5) + '"/>');
      var nameY = g.top - (g.narrow ? 22 : 28);
      if (bed && bed.height) {
        var topY = g.floor - bed.height * g.s;
        nameY = topY - (g.narrow ? 22 : 28);
        o.push('<text class="tl-t1" x="' + r1(mid) + '" y="' + r1(nameY) + '" text-anchor="middle">' + esc(g.narrow ? (sl.tier.short || sl.tier.name) : sl.tier.name) + '</text>');
        o.push('<text class="tl-t2" x="' + r1(mid) + '" y="' + r1(nameY + (g.narrow ? 14 : 17)) + '" text-anchor="middle">' + esc(inch(bed.height)) + '</text>');
        if (sk && sk.depths) {
          var newSet = i > 0 && stacks[i - 1] && stacks[i - 1].depths ? added(stacks[i - 1], sk) : {};
          for (j = 0; j < sk.list.length; j++) o.push(band(sk.list[j], x, g.bw, g, (st.narrow ? 'n' : 'w') + i + '_' + j, !!newSet[sk.list[j].key + '|' + sk.list[j].occ], i));
          o.push('<path class="tl-quilt" d="M' + r1(x) + ' ' + r1(topY + 1) + 'h' + g.bw + '"/>');
        } else {
          var hh = bed.height * g.s;
          o.push('<g class="tl-l tl-np" data-k="' + i + '|body|1" data-key="body" data-x="' + r1(x) + '" data-y="' + r1(topY) + '" data-w="' + g.bw + '" data-h="' + r1(hh) + '">');
          o.push('<rect class="tl-b" x="' + r1(x) + '" y="' + r1(topY) + '" width="' + g.bw + '" height="' + r1(hh) + '" fill="#2f2c2a"/>');
          o.push('<rect x="' + r1(x) + '" y="' + r1(topY) + '" width="' + g.bw + '" height="' + r1(hh) + '" fill="url(#tl-hatch-' + (st.narrow ? 'n' : 'w') + ')"/>');
          var lines = g.narrow ? ['Depths not', 'published'] : ['Layer depths', 'not published'];
          var names = [], lowB = {}, prevB = i > 0 ? st.slots[i - 1].bed : null;
          if (prevB && prevB.height) (prevB.layers || []).forEach(function (l) { lowB[String(l[0]).split(' (')[0].toLowerCase()] = 1; });
          if (!g.narrow) (bed.layers || []).forEach(function (l, li) {
            var base = String(l[0]).split(' (')[0], nw = prevB && prevB.height && !lowB[base.toLowerCase()];
            wrap((nw ? '+ ' : (li + 1) + '. ') + base, 21).forEach(function (w2, wi) { names.push({ t: (wi ? '   ' : '') + w2, n: nw }); });
          });
          var lh = g.narrow ? 13 : 15, total = (lines.length + (names.length ? names.length + 0.6 : 0)) * lh, y0 = topY + hh / 2 - total / 2 + lh * 0.75;
          lines.forEach(function (t, ti) { o.push('<text class="tl-np-h" x="' + r1(mid) + '" y="' + r1(y0 + ti * lh) + '" text-anchor="middle">' + esc(t) + '</text>'); });
          names.forEach(function (t, ti) {
            var ty2 = y0 + (lines.length + 0.6 + ti) * lh;
            if (t.n) o.push('<g class="tl-l is-new tl-np-new" data-k="' + i + '|np|' + ti + '" data-key="np' + ti + '" data-x="' + r1(x) + '" data-y="' + r1(ty2 - lh + 3) + '" data-w="' + g.bw + '" data-h="' + lh + '"><text class="tl-np-l tl-np-l--add" x="' + r1(x + 10) + '" y="' + r1(ty2) + '">' + esc(t.t) + '</text></g>');
            else o.push('<text class="tl-np-l" x="' + r1(x + 10) + '" y="' + r1(ty2) + '">' + esc(t.t) + '</text>');
          });
          o.push('</g>');
        }
        o.push('<rect class="tl-edge" x="' + r1(x) + '" y="' + r1(topY) + '" width="' + g.bw + '" height="' + r1(bed.height * g.s) + '" rx="3"/>');
        o.push('<text class="tl-t3" x="' + r1(mid) + '" y="' + r1(g.floor + (g.narrow ? 15 : 19)) + '" text-anchor="middle">' + esc(g.narrow ? '' : (bed.short || bed.name)) + '</text>');
      } else {
        // nothing to draw: say why, at the floor, under the bed's own photo when there is one
        var msg = sl.empty || 'Not drawn';
        var ml = wrap(msg, g.narrow ? 11 : 20), lh2 = g.narrow ? 13 : 15, my = g.floor - 18 - ml.length * lh2;
        if (bed && bed.img) {
          var iw = g.bw, ih = Math.round(g.bw * 0.66), iy = my - ih - (g.narrow ? 26 : 34), cid = 'tlp' + (st.narrow ? 'n' : 'w') + i;
          o.push('<clipPath id="' + cid + '"><rect x="' + r1(x) + '" y="' + r1(iy) + '" width="' + iw + '" height="' + ih + '" rx="6"/></clipPath>');
          o.push('<image class="tl-photo" href="' + esc(bed.img) + '" x="' + r1(x) + '" y="' + r1(iy) + '" width="' + iw + '" height="' + ih + '" preserveAspectRatio="xMidYMid slice" clip-path="url(#' + cid + ')"/>');
          if (!g.narrow) o.push('<text class="tl-t3" x="' + r1(mid) + '" y="' + r1(iy + ih + 16) + '" text-anchor="middle">' + esc(bed.short || bed.name) + '</text>');
          my += 2;
        }
        o.push('<text class="tl-t1" x="' + r1(mid) + '" y="' + r1((bed && bed.img ? my - (g.narrow ? 26 : 34) - Math.round(g.bw * 0.66) : my) - (g.narrow ? 10 : 14)) + '" text-anchor="middle">' + esc(g.narrow ? (sl.tier.short || sl.tier.name) : sl.tier.name) + '</text>');
        ml.forEach(function (t, ti) { o.push('<text class="tl-empty" x="' + r1(mid) + '" y="' + r1(my + 6 + ti * lh2) + '" text-anchor="middle">' + esc(t) + '</text>'); });
        o.push('<rect class="tl-plinth" x="' + r1(x) + '" y="' + r1(g.floor - 10) + '" width="' + g.bw + '" height="10" rx="2"/>');
      }
      o.push('</g>');
    }
    o.push('<path class="tl-floor" d="M' + (rx) + ' ' + r1(g.floor) + 'H' + (g.W - 2) + '"/>');
    o.push('</svg>');
    return o.join('');
  }

  // what changes from tier A to tier B, from the same layer data as the drawing
  function pair(A, B) {
    var out = { a: A, b: B, ok: false, dh: 0, adds: [], deeper: [], ma: 0, mb: 0, same: '', none: '' };
    if (!A.bed || !B.bed || !A.bed.height || !B.bed.height) {
      var why = (!B.bed || !B.bed.height) ? B : A;
      out.none = why.tier.name + ': ' + (why.empty || 'nothing to compare in this feel.').replace(/^./, function (c) { return c.toLowerCase(); });
      return out;
    }
    out.ok = true;
    out.dh = Math.round((B.bed.height - A.bed.height) * 100) / 100;
    var sa = stack(A.bed), sb = stack(B.bed);
    out.depths = sa.depths && sb.depths;
    if (!out.depths) return out;
    var nw = added(sa, sb), seen = {}, tot = function (sx, key) { var t = 0; sx.list.forEach(function (k) { if (k.key === key) t += k.d; }); return t; };
    sb.list.slice().reverse().forEach(function (k) {
      if (!nw[k.key + '|' + k.occ]) return;
      var lab = k.name + ' ' + inch(k.d);
      if (seen[lab]) { seen[lab].n++; return; }
      seen[lab] = { n: 1, k: k, lab: lab };
    });
    var RANK = { micro: 0, latex: 1, mem: 2, foam: 3, base: 4, core: 5 };
    out.adds = Object.keys(seen).map(function (l) { return seen[l]; });
    out.adds.sort(function (x, y) { return (RANK[x.k.kind] - RANK[y.k.kind]) || (y.k.d * y.n - x.k.d * x.n); });
    var done = {};
    sb.list.forEach(function (k) {
      if (done[k.key] || k.kind === 'core' || k.kind === 'base' || k.kind === 'micro') return;
      done[k.key] = 1;
      var ta = tot(sa, k.key), tb = tot(sb, k.key);
      if (ta > 0 && tb - ta >= 0.75) out.deeper.push({ name: k.name, from: ta, to: tb, key: k.key });
    });
    out.ma = sa.list.filter(function (k) { return k.kind === 'micro'; }).length;
    out.mb = sb.list.filter(function (k) { return k.kind === 'micro'; }).length;
    var cA = sa.list.filter(function (k) { return k.kind === 'core'; })[0], cB = sb.list.filter(function (k) { return k.kind === 'core'; })[0];
    if (cA && cB && cA.key === cB.key && cA.d === cB.d) out.same = 'Same ' + inch(cB.d) + ' coil core.' + (out.mb > out.ma ? ' Coil layers ' + (1 + out.ma) + ' to ' + (1 + out.mb) + '.' : '');
    return out;
  }

  function hTxt(dh) { return dh > 0 ? inch(dh) + ' taller' : dh < 0 ? inch(-dh) + ' lower' : 'the same height'; }
  var ORD = ['', 'a', 'a second', 'a third', 'a fourth'];

  // the chips for one tier column: what it adds over the tier to its left
  function stepChips(st, i) {
    var P = pair(st.slots[i - 1], st.slots[i]), chips = [];
    if (!P.ok) return P.b.tier.status === 'gone' && P.b.bed ? '' : '<p class="tl-step__none">' + esc(P.none) + '</p>';
    chips.push('<span class="tl-chip tl-chip--h">' + (P.dh > 0 ? '+' + inch(P.dh) + ' taller' : P.dh < 0 ? inch(-P.dh) + ' lower' : 'Same height') + '</span>');
    if (P.depths) {
      P.adds.slice(0, 3).forEach(function (a) {
        chips.push('<span class="tl-chip tl-chip--add' + (a.k.kind === 'micro' ? ' tl-chip--coil' : '') + '" data-key="' + esc(a.k.key) + '">+ ' + (a.n > 1 ? a.n + ' × ' : '') + esc(a.lab) + '</span>');
      });
      P.deeper.slice(0, 1).forEach(function (d) { chips.push('<span class="tl-chip tl-chip--add" data-key="' + esc(d.key) + '">' + esc(d.name + ' ' + num(d.from) + ' to ' + inch(d.to)) + '</span>'); });
      var more = P.adds.length - 3;
      if (more > 0) chips.push('<span class="tl-chip tl-chip--more">+ ' + more + ' more ' + (more > 1 ? 'additions' : 'addition') + ' in the layer list</span>');
      if (P.same) chips.push('<span class="tl-chip tl-chip--same">' + esc(P.same) + '</span>');
    } else if (st.adds && st.adds[P.b.tier.key]) {
      st.adds[P.b.tier.key].forEach(function (a) { chips.push('<span class="tl-chip tl-chip--add">+ ' + esc(a.replace(/^(a|an) /, '').replace(/^./, function (c) { return c.toUpperCase(); })) + '</span>'); });
    }
    return '<p class="tl-step__k">Over ' + esc(P.a.tier.name) + '</p><p class="tl-cap__chips">' + chips.join(' ') + '</p>';
  }

  // the steps in one or two plain sentences
  function summary(st) {
    var o = [], i;
    for (i = 1; i < st.slots.length; i++) {
      var P = pair(st.slots[i - 1], st.slots[i]);
      var lead = '<b>' + esc(P.a.tier.name) + ' to ' + esc(P.b.tier.name) + ':</b> ';
      if (!P.ok && P.b.tier.status === 'gone' && P.b.bed) continue;
      if (!P.ok) { o.push('<li>' + lead + esc(P.none.replace(/^[^:]*: /, '').replace(/^./, function (c) { return c.toUpperCase(); })) + '</li>'); continue; }
      var bits = [];
      if (P.depths) {
        if (P.mb > P.ma) bits.push((ORD[P.mb - P.ma > 1 ? 1 : P.mb] || 'more') + ' layer of ' + P.adds.filter(function (a) { return a.k.kind === 'micro'; }).map(function (a) { return a.k.name; })[0]);
        P.adds.forEach(function (a) { if (a.k.kind === 'latex' && bits.length < 2) bits.push(a.k.name); });
        P.deeper.forEach(function (d) { if (bits.length < 2) bits.push(inch(d.to) + ' of ' + d.name + ', up from ' + inch(d.from)); });
        P.adds.forEach(function (a) { if (bits.length < 2 && a.k.kind !== 'micro' && a.k.kind !== 'latex') bits.push(a.k.name); });
        if (P.mb > P.ma && P.mb - P.ma > 1) bits[0] = (P.mb - P.ma) + ' layers of ' + P.adds.filter(function (a) { return a.k.kind === 'micro'; })[0].k.name;
      } else if (st.adds && st.adds[P.b.tier.key]) {
        bits = st.adds[P.b.tier.key].slice(0, 2);
      }
      var txt = hTxt(P.dh).replace(/^./, function (c) { return c.toUpperCase(); }) + (bits.length ? ', with ' + bits.join(' and ') : '');
      o.push('<li>' + lead + esc(/\.$/.test(txt) ? txt : txt + '.') + '</li>');
    }
    var first = st.slots.filter(function (x) { return x.bed && x.bed.height; })[0];
    var k = first ? '<p class="tl-sum__k">' + esc(first.bed.short) + ', tier to tier</p>' : '';
    return k + '<ul class="tl-sum__list">' + o.join('') + '</ul>';
  }

  // every layer, top down, per tier, with the new ones marked
  function list(st) {
    var o = [], i;
    for (i = 0; i < st.slots.length; i++) {
      var sl = st.slots[i], bed = sl.bed;
      o.push('<div class="tl-list__col"><p class="tl-list__h">' + esc(sl.tier.name) + (bed && bed.height ? ' <span>' + esc(bed.short || '') + ', ' + esc(inch(bed.height)) + '</span>' : '') + '</p>');
      if (!bed || !bed.height) { o.push('<p class="tl-list__none">' + esc(sl.empty || 'Nothing to list.') + '</p></div>'); continue; }
      var sk = stack(bed), prev = null;
      for (var p = i - 1; p >= 0 && !prev; p--) if (st.slots[p].bed && st.slots[p].bed.height) prev = st.slots[p];
      var nw = prev && sk.depths ? added(stack(prev.bed), sk) : {};
      o.push('<ol class="tl-list__ol">');
      if (sk.depths) {
        (bed.layers || []).forEach(function (l) {
          if (l[2] === 'cover') { o.push('<li class="tl-list__li"><span>' + esc(l[0]) + '</span><span class="tl-list__d">' + (l[1] != null ? inch(l[1]) : '') + '</span></li>'); return; }
        });
        sk.list.slice().reverse().forEach(function (k) {
          var isN = nw[k.key + '|' + k.occ];
          o.push('<li class="tl-list__li' + (isN ? ' is-new' : '') + '" data-key="' + esc(k.key) + '"><span>' + (isN ? '<b class="tl-list__new">Added</b> ' : '') + esc(k.name) + '</span><span class="tl-list__d">' + inch(k.d) + '</span></li>');
        });
      } else {
        (bed.layers || []).forEach(function (l) { o.push('<li class="tl-list__li"><span>' + esc(l[0]) + '</span></li>'); });
      }
      o.push('</ol>');
      if (!sk.depths) o.push('<p class="tl-list__fit">Height from the maker; layer depths not published.</p>');
      if (sk.depths && Math.abs(sk.printed - bed.height) > 0.051) o.push('<p class="tl-list__fit">The printed layers add up to ' + esc(num(sk.printed)) + ' in. and the bed is ' + esc(num(bed.height)) + ' in., so the drawing keeps the bed at ' + esc(num(bed.height)) + ' in. and draws the layers above the coils in proportion.</p>');
      o.push('</div>');
    }
    return o.join('');
  }

  // the bed each tier shows for a feel and a build, and what to say where there is none
  // data: { tiers: [{key, name, short, status, optional}], beds: [{id, tier, build, feel, height, ...}], feels, builds, extra }
  function slots(data, feel, build, extra) {
    var lab = function (arr, k) { for (var i = 0; i < arr.length; i++) if (arr[i].key === k) return arr[i].label; return k; };
    var fl = lab(data.feels, feel), bl = lab(data.builds, build).toLowerCase();
    return data.tiers.filter(function (t) { return !t.optional || extra; }).map(function (t) {
      var inFeel = data.beds.filter(function (b) { return b.tier === t.key && b.feel === feel; });
      var hit = inFeel.filter(function (b) { return b.build === build || b.build === 'any'; });
      hit.sort(function (a, b) { return (b.height ? 1 : 0) - (a.height ? 1 : 0); });
      var bed = hit[0] || null, empty = '';
      if (!bed) empty = inFeel.length ? 'No ' + fl + ' ' + bl + ' in ' + t.name + '.' : 'No ' + fl + ' in ' + t.name + '.';
      else if (!bed.height) empty = t.status === 'gone' ? 'No longer on the maker’s site, so no maker height to draw.' : 'Not on the maker’s site, so no maker height to draw.';
      return { tier: t, bed: bed, empty: empty, others: inFeel.filter(function (b) { return b !== bed; }) };
    });
  }

  function maxH(data) { var m = 0; data.beds.forEach(function (b) { if (b.height > m) m = b.height; }); return m; }

  var api = { svg: svg, stepChips: stepChips, summary: summary, pair: pair, list: list, stack: stack, added: added, canon: canon, G: G, slots: slots, maxH: maxH };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.MOTierDraw = api;
})(this);
